import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Token as TokenIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface Plan {
  id: number;
  name: string;
  description: string;
  price: number;
  tokens_per_month: number;
  max_benefits: number;
  features: string[];
  is_active: boolean;
  subscriber_count?: number;
  created_at?: string;
  updated_at?: string;
}

const PlanManagement: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Form state
  const [formData, setFormData] = useState<Partial<Plan>>({
    name: '',
    description: '',
    price: 0,
    tokens_per_month: 0,
    max_benefits: 0,
    features: [],
    is_active: true,
  });

  // Default plans data
  const defaultPlans: Plan[] = [
    {
      id: 1,
      name: 'FREE',
      description: 'Basic access to the platform',
      price: 0,
      tokens_per_month: 100,
      max_benefits: 5,
      features: ['Basic Benefits', 'Limited Games', 'Community Access'],
      is_active: true,
      subscriber_count: 1250,
    },
    {
      id: 2,
      name: 'Essential',
      description: 'Enhanced experience for active users',
      price: 99,
      tokens_per_month: 500,
      max_benefits: 15,
      features: ['All Basic Features', 'Priority Support', 'Exclusive Benefits', 'Monthly Giveaways'],
      is_active: true,
      subscriber_count: 890,
    },
    {
      id: 3,
      name: 'Premium',
      description: 'Premium access with advanced features',
      price: 199,
      tokens_per_month: 1500,
      max_benefits: 50,
      features: ['All Essential Features', 'Premium Benefits', 'VIP Support', 'Weekly Giveaways', 'Agent Referrals'],
      is_active: true,
      subscriber_count: 420,
    },
    {
      id: 4,
      name: 'Ultimate',
      description: 'Complete access to all platform features',
      price: 399,
      tokens_per_month: 5000,
      max_benefits: -1, // Unlimited
      features: ['All Premium Features', 'Unlimited Benefits', '24/7 Support', 'Daily Giveaways', 'Custom Agent Network', 'Analytics Dashboard'],
      is_active: true,
      subscriber_count: 150,
    },
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/plans');
      setPlans(response.data || defaultPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Use default plans on error
      setPlans(defaultPlans);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setFormData({ ...plan, features: [...plan.features] });
    setEditDialogOpen(true);
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      tokens_per_month: 0,
      max_benefits: 0,
      features: [],
      is_active: true,
    });
    setEditDialogOpen(true);
  };

  const handleSavePlan = async () => {
    try {
      if (selectedPlan) {
        await api.put(`/admin/plans/${selectedPlan.id}`, formData);
      } else {
        await api.post('/admin/plans', formData);
      }

      setSnackbar({
        open: true,
        message: `Plan ${selectedPlan ? 'updated' : 'created'} successfully`,
        severity: 'success'
      });
      fetchPlans();
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error saving plan:', error);
      setSnackbar({ open: true, message: 'Error saving plan', severity: 'error' });
    }
  };

  const handleTogglePlan = async (planId: number, isActive: boolean) => {
    try {
      await api.put(`/admin/plans/${planId}`, { is_active: isActive });
      setPlans(plans.map(p => p.id === planId ? { ...p, is_active: isActive } : p));
      setSnackbar({
        open: true,
        message: `Plan ${isActive ? 'enabled' : 'disabled'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling plan:', error);
      // Still update local state for demo mode
      setPlans(plans.map(p => p.id === planId ? { ...p, is_active: isActive } : p));
      setSnackbar({ open: true, message: 'Plan updated locally', severity: 'success' });
    }
  };

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...(formData.features || [])];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...(formData.features || []), ''] });
  };

  const removeFeature = (index: number) => {
    const newFeatures = formData.features?.filter((_, i) => i !== index) || [];
    setFormData({ ...formData, features: newFeatures });
  };

  const getTierColor = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'free': return '#4CAF50';
      case 'essential': return '#2196F3';
      case 'premium': return '#FF9800';
      case 'ultimate': return '#9C27B0';
      default: return '#757575';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', { 
      style: 'currency', 
      currency: 'ZAR' 
    }).format(amount);
  };

  const totalSubscribers = plans.reduce((sum, plan) => sum + (plan.subscriber_count || 0), 0);
  const totalRevenue = plans.reduce((sum, plan) => sum + (plan.price * (plan.subscriber_count || 0)), 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
            Plan Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#888' }}>
            Manage subscription tiers, pricing, and token allocations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreatePlan}
          sx={{
            bgcolor: '#FFD700',
            color: '#0A0A0A',
            '&:hover': { bgcolor: '#FFC107' },
          }}
        >
          Create Plan
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon sx={{ color: '#4CAF50', mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {totalSubscribers.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Total Subscribers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <MoneyIcon sx={{ color: '#FFD700', mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {formatCurrency(totalRevenue)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Monthly Revenue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TokenIcon sx={{ color: '#2196F3', mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {plans.reduce((sum, plan) => sum + (plan.tokens_per_month * (plan.subscriber_count || 0)), 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Tokens/Month Allocated
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon sx={{ color: '#FF9800', mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {plans.filter(p => p.is_active).length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Active Plans
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Plans Table */}
      <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
            Subscription Plans
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#2A2A2A', color: 'white' } }}>
                  <TableCell>Plan</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Tokens/Month</TableCell>
                  <TableCell>Max Benefits</TableCell>
                  <TableCell>Subscribers</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow 
                    key={plan.id} 
                    sx={{ 
                      '& .MuiTableCell-body': { 
                        color: 'white',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      } 
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: getTierColor(plan.name),
                            mr: 2,
                          }}
                        />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {plan.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#888' }}>
                            {plan.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
                        {plan.price === 0 ? 'Free' : formatCurrency(plan.price)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${plan.tokens_per_month.toLocaleString()} tokens`}
                        size="small"
                        sx={{ bgcolor: '#2196F3', color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {plan.max_benefits === -1 ? 'Unlimited' : plan.max_benefits}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {(plan.subscriber_count || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={plan.is_active}
                        onChange={(e) => handleTogglePlan(plan.id, e.target.checked)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit Plan">
                          <IconButton 
                            onClick={() => handleEditPlan(plan)}
                            sx={{ color: '#FFD700' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton sx={{ color: '#2196F3' }}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Plan Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1A1A1A',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          {selectedPlan ? 'Edit Plan' : 'Create New Plan'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Plan Name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { color: 'white' },
                  '& .MuiInputLabel-root': { color: '#888' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#888' }}>Price</InputLabel>
                <OutlinedInput
                  value={formData.price || 0}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  startAdornment={<InputAdornment position="start">R</InputAdornment>}
                  label="Price"
                  type="number"
                  sx={{ color: 'white' }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { color: 'white' },
                  '& .MuiInputLabel-root': { color: '#888' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tokens per Month"
                type="number"
                value={formData.tokens_per_month || 0}
                onChange={(e) => setFormData({ ...formData, tokens_per_month: Number(e.target.value) })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { color: 'white' },
                  '& .MuiInputLabel-root': { color: '#888' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Benefits (-1 for unlimited)"
                type="number"
                value={formData.max_benefits || 0}
                onChange={(e) => setFormData({ ...formData, max_benefits: Number(e.target.value) })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { color: 'white' },
                  '& .MuiInputLabel-root': { color: '#888' },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Features
              </Typography>
              {formData.features?.map((feature, index) => (
                <Box key={index} display="flex" alignItems="center" gap={2} mb={2}>
                  <TextField
                    fullWidth
                    label={`Feature ${index + 1}`}
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { color: 'white' },
                      '& .MuiInputLabel-root': { color: '#888' },
                    }}
                  />
                  <Button
                    onClick={() => removeFeature(index)}
                    color="error"
                    size="small"
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button onClick={addFeature} startIcon={<AddIcon />} sx={{ mt: 1 }}>
                Add Feature
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSavePlan}
            variant="contained"
            sx={{ bgcolor: '#FFD700', color: '#0A0A0A' }}
          >
            {selectedPlan ? 'Update' : 'Create'} Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanManagement;